<template>
  <div class="lw">
    <!-- 头部 -->
    <div class="lw-head">
      <div class="lw-title">🍀 运气世界</div>
      <div class="lw-score">
        <span>总分</span>
        <b>{{ score }}</b>
      </div>
    </div>

    <!-- 玩法页签 -->
    <div class="lw-tabs">
      <button
          v-for="t in tabs"
          :key="t.id"
          type="button"
          class="lw-tab"
          :class="{ active: tab === t.id }"
          @click="switchTab(t.id)">
        <span class="lw-tab-icon">{{ t.icon }}</span>{{ t.name }}
      </button>
    </div>

    <div class="lw-body">
      <!-- ============ ① 猜礼物盒 ============ -->
      <div v-if="tab === 'box'" class="lw-play">
        <p class="lw-play-title">🎁 小球藏进了哪个礼物盒？</p>
        <div class="lw-box-row">
          <transition-group name="lw-move" tag="div" class="lw-box-row">
            <div
                v-for="(box, i) in boxes"
                :key="box.id"
                class="lw-box"
                :class="{ reveal: revealed && ballBox === i }"
                @click="guessBox(i)">
              <span class="lw-box-emoji">{{ box.opened ? (ballBox === i ? '🎉' : '📦') : '🎁' }}</span>
            </div>
          </transition-group>
        </div>
        <p class="lw-msg" :class="{ ok: boxMsgOk }">{{ boxMsg }}</p>
        <div class="lw-actions">
          <button type="button" class="lw-btn" :disabled="shuffling || revealed" @click="shuffleBoxes">
            🔀 {{ shuffling ? '洗牌中…' : '洗牌' }}
          </button>
          <button type="button" class="lw-btn lw-btn-ghost" @click="resetBox">🎁 重藏小球</button>
        </div>
      </div>

      <!-- ============ ② 猜骰子 ============ -->
      <div v-if="tab === 'dice'" class="lw-play">
        <p class="lw-play-title">🎲 猜猜骰子点数（小 1-3 / 大 4-6）</p>
        <div class="lw-dice" :class="{ rolling: diceRolling }">
          <span>{{ diceValue }}</span>
        </div>
        <div class="lw-choice">
          <button
              v-for="op in diceOps"
              :key="op.id"
              type="button"
              class="lw-btn"
              :class="{ picked: dicePick === op.id }"
              :disabled="diceRolling"
              @click="dicePick = op.id">
            {{ op.label }}
            <em>{{ op.odds }}</em>
          </button>
        </div>
        <div v-if="dicePick === 'exact'" class="lw-choice">
          <button
              v-for="n in 6"
              :key="n"
              type="button"
              class="lw-btn lw-num"
              :class="{ picked: exactNum === n }"
              :disabled="diceRolling"
              @click="exactNum = n">{{ n }}</button>
        </div>
        <p class="lw-msg" :class="{ ok: diceMsgOk }">{{ diceMsg }}</p>
        <button type="button" class="lw-btn" :disabled="diceRolling || !dicePick || (dicePick === 'exact' && !exactNum)" @click="rollDice">
          🎲 {{ diceRolling ? '摇骰中…' : '开摇' }}
        </button>
      </div>

      <!-- ============ ③ 猜硬币 ============ -->
      <div v-if="tab === 'coin'" class="lw-play">
        <p class="lw-play-title">🪙 硬币落下是正还是反？</p>
        <div class="lw-coin" :class="{ flip: coinFlipping }">
          <div class="lw-coin-face lw-coin-front">🌙</div>
          <div class="lw-coin-face lw-coin-back">☀️</div>
        </div>
        <div class="lw-choice">
          <button
              type="button"
              class="lw-btn"
              :class="{ picked: coinPick === 'front' }"
              :disabled="coinFlipping"
              @click="coinPick = 'front'">🌙 正面</button>
          <button
              type="button"
              class="lw-btn"
              :class="{ picked: coinPick === 'back' }"
              :disabled="coinFlipping"
              @click="coinPick = 'back'">☀️ 反面</button>
        </div>
        <p class="lw-msg" :class="{ ok: coinMsgOk }">{{ coinMsg }}</p>
        <button type="button" class="lw-btn" :disabled="coinFlipping || !coinPick" @click="flipCoin">
          🪙 {{ coinFlipping ? '抛掷中…' : '抛硬币' }}
        </button>
      </div>

      <!-- ============ ④ 幸运转盘 ============ -->
      <div v-if="tab === 'wheel'" class="lw-play">
        <p class="lw-play-title">🎡 转动幸运转盘赢分数</p>
        <div class="lw-wheel-wrap">
          <div class="lw-pointer">▼</div>
          <div class="lw-wheel" :style="{ transform: 'rotate(' + wheelDeg + 'deg)' }">
            <span
                v-for="(s, i) in sectors"
                :key="s.label"
                class="lw-sector"
                :style="{ transform: 'rotate(' + (i * 45 + 22.5) + 'deg) translateY(-72px) rotate(' + -(i * 45 + 22.5) + 'deg)' }">
              {{ s.label }}
            </span>
          </div>
        </div>
        <p class="lw-msg" :class="{ ok: wheelMsgOk }">{{ wheelMsg }}</p>
        <button type="button" class="lw-btn" :disabled="spinning" @click="spinWheel">
          🎡 {{ spinning ? '转动中…' : '转动' }}
        </button>
      </div>
    </div>

    <p class="lw-tip">运气游戏，试试今天的手气吧～</p>
  </div>
</template>

<script>
/* =====================================================
 * 运气世界（运气小游戏合集）
 * ① 猜礼物盒（三盒藏球 + 洗牌动画）
 * ② 猜骰子（大小 / 具体点数）
 * ③ 猜硬币（正反）
 * ④ 幸运转盘（8 扇区抽奖）
 * ===================================================== */
const SECTORS = [
  { label: '+5', value: 5, color: '#ff9ecb' },
  { label: '+10', value: 10, color: '#ffd98a' },
  { label: '+20', value: 20, color: '#c9a6ff' },
  { label: '0', value: 0, color: '#8fe3c9' },
  { label: '+50', value: 50, color: '#7ec8ff' },
  { label: '+10', value: 10, color: '#ffab9e' },
  { label: '+100', value: 100, color: '#ffb3e6' },
  { label: '+30', value: 30, color: '#ffe6a8' }
]

export default {
  name: 'LuckWorld',
  data () {
    return {
      score: 0,
      tab: 'box',
      tabs: [
        { id: 'box', name: '猜礼物盒', icon: '🎁' },
        { id: 'dice', name: '猜骰子', icon: '🎲' },
        { id: 'coin', name: '猜硬币', icon: '🪙' },
        { id: 'wheel', name: '幸运转盘', icon: '🎡' }
      ],

      /* ---------- ① 猜礼物盒 ---------- */
      boxes: [
        { id: 0, opened: false },
        { id: 1, opened: false },
        { id: 2, opened: false }
      ],
      ballBox: 0,
      shuffling: false,
      revealed: false,
      boxMsg: '选一个礼物盒，看看小球藏在哪～',
      boxMsgOk: false,
      shuffleTimer: null,

      /* ---------- ② 猜骰子 ---------- */
      diceOps: [
        { id: 'small', label: '小 (1-3)', odds: 'x2' },
        { id: 'big', label: '大 (4-6)', odds: 'x2' },
        { id: 'exact', label: '猜点数', odds: 'x6' }
      ],
      dicePick: null,
      exactNum: null,
      diceValue: 1,
      diceRolling: false,
      diceMsg: '选一个下注方式，开摇吧～',
      diceMsgOk: false,
      diceTimer: null,

      /* ---------- ③ 猜硬币 ---------- */
      coinPick: null,
      coinResult: 'front',
      coinFlipping: false,
      coinMsg: '选正面还是反面？',
      coinMsgOk: false,
      coinTimer: null,

      /* ---------- ④ 幸运转盘 ---------- */
      sectors: SECTORS,
      wheelDeg: 0,
      spinning: false,
      wheelMsg: '点击转动试试手气！',
      wheelMsgOk: false,
      wheelTimer: null
    }
  },
  created () {
    this.resetBox()
  },
  beforeDestroy () {
    this.clearTimers()
  },
  methods: {
    switchTab (id) {
      this.tab = id
    },
    clearTimers () {
      if (this.shuffleTimer) { clearInterval(this.shuffleTimer); this.shuffleTimer = null }
      if (this.diceTimer) { clearInterval(this.diceTimer); this.diceTimer = null }
      if (this.coinTimer) { clearTimeout(this.coinTimer); this.coinTimer = null }
      if (this.wheelTimer) { clearTimeout(this.wheelTimer); this.wheelTimer = null }
    },

    /* ================= ① 猜礼物盒 ================= */
    resetBox () {
      this.clearTimers()
      this.shuffling = false
      this.revealed = false
      this.ballBox = Math.floor(Math.random() * 3)
      this.boxes = [
        { id: 0, opened: false },
        { id: 1, opened: false },
        { id: 2, opened: false }
      ]
      this.boxMsg = '选一个礼物盒，看看小球藏在哪～'
      this.boxMsgOk = false
    },
    shuffleBoxes () {
      if (this.shuffling || this.revealed) return
      this.shuffling = true
      this.revealed = false
      this.boxes = this.boxes.map(b => ({ ...b, opened: false }))
      this.boxMsg = '🔀 洗牌中…'
      this.boxMsgOk = false
      let count = 0
      this.shuffleTimer = setInterval(() => {
        // 随机交换两个位置
        const a = Math.floor(Math.random() * 3)
        let b = Math.floor(Math.random() * 3)
        while (b === a) b = Math.floor(Math.random() * 3)
        const t = this.boxes[a]
        this.$set(this.boxes, a, this.boxes[b])
        this.$set(this.boxes, b, t)
        count++
        if (count >= 7) {
          clearInterval(this.shuffleTimer)
          this.shuffleTimer = null
          this.shuffling = false
          this.boxMsg = '洗好啦！小球藏进了其中一个盒子，猜猜是哪个？'
        }
      }, 180)
    },
    guessBox (i) {
      if (this.shuffling || this.revealed) return
      this.revealed = true
      this.$set(this.boxes, i, { ...this.boxes[i], opened: true })
      if (i === this.ballBox) {
        this.score += 20
        this.boxMsg = '🎉 猜中啦！小球就在这个盒子里 +20'
        this.boxMsgOk = true
      } else {
        this.$set(this.boxes, this.ballBox, { ...this.boxes[this.ballBox], opened: true })
        this.boxMsg = '😢 猜错啦～小球在另一个盒子里'
        this.boxMsgOk = false
      }
    },

    /* ================= ② 猜骰子 ================= */
    rollDice () {
      if (this.diceRolling || !this.dicePick) return
      this.diceRolling = true
      this.diceMsg = '🎲 骰子滚动中…'
      this.diceMsgOk = false
      let count = 0
      this.diceTimer = setInterval(() => {
        this.diceValue = Math.floor(Math.random() * 6) + 1
        count++
        if (count >= 10) {
          clearInterval(this.diceTimer)
          this.diceTimer = null
          this.diceRolling = false
          this.settleDice()
        }
      }, 90)
    },
    settleDice () {
      const v = this.diceValue
      let won = false
      let pts = 0
      if (this.dicePick === 'small') {
        won = v <= 3
        pts = 10
      } else if (this.dicePick === 'big') {
        won = v >= 4
        pts = 10
      } else {
        // 猜具体点数
        won = v === this.exactNum
        pts = 30
      }
      // 猜具体点数：先弹出输入？简化为玩家在上轮输赢后可选
      if (this.dicePick === 'exact') {
        this.diceMsg = `骰子是 ${v} 点` + (won ? '，你猜中了！+30' : '，可惜没猜中')
      } else {
        this.diceMsg = `骰子是 ${v} 点（${v <= 3 ? '小' : '大'}）` + (won ? '，你猜中了！+10' : '，可惜没猜中')
      }
      if (won) {
        this.score += pts
        this.diceMsgOk = true
      } else {
        this.diceMsgOk = false
      }
    },

    /* ================= ③ 猜硬币 ================= */
    flipCoin () {
      if (this.coinFlipping || !this.coinPick) return
      this.coinFlipping = true
      this.coinMsg = '🪙 硬币旋转中…'
      this.coinMsgOk = false
      this.coinResult = Math.random() < 0.5 ? 'front' : 'back'
      this.coinTimer = setTimeout(() => {
        this.coinFlipping = false
        const won = this.coinPick === this.coinResult
        const face = this.coinResult === 'front' ? '🌙 正面' : '☀️ 反面'
        if (won) {
          this.score += 10
          this.coinMsg = `🎉 ${face}！你猜中了 +10`
          this.coinMsgOk = true
        } else {
          this.coinMsg = `😢 是${face}，没猜中`
          this.coinMsgOk = false
        }
      }, 900)
    },

    /* ================= ④ 幸运转盘 ================= */
    spinWheel () {
      if (this.spinning) return
      this.spinning = true
      this.wheelMsg = '🎡 转盘转动中…'
      this.wheelMsgOk = false
      const extra = Math.floor(Math.random() * 360)
      const target = this.wheelDeg + 360 * 5 + extra
      this.wheelDeg = target
      this.wheelTimer = setTimeout(() => {
        this.spinning = false
        const deg = target % 360
        const idx = Math.floor(deg / 45)
        const s = this.sectors[idx]
        this.score += s.value
        this.wheelMsg = `🎉 抽到「${s.label}」${s.value > 0 ? '+' + s.value + ' 分' : '，没得分'}`
        this.wheelMsgOk = s.value > 0
      }, 2200)
    }
  }
}
</script>

<style scoped>
.lw {
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: #6b4a5a;
  max-width: 480px;
  margin: 0 auto;
}
.lw-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.lw-title {
  font-size: 22px;
  font-weight: 800;
  color: #e25b96;
  letter-spacing: 1px;
}
.lw-score {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #ffe9f3, #ffd3e6);
  border: 1px solid #ffc3da;
}
.lw-score span { font-size: 12px; color: #c490a8; }
.lw-score b { font-size: 20px; color: #ff5fa0; }

/* 页签 */
.lw-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.lw-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  padding: 7px 16px;
  background: #fff;
  color: #b06a8a;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.lw-tab:hover { border-color: #ff8fc7; }
.lw-tab.active {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  border-color: #ff6fa5;
  color: #fff;
  box-shadow: 0 4px 12px rgba(255, 111, 165, 0.35);
}
.lw-tab-icon { font-size: 15px; }

/* 玩法区 */
.lw-body {
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(165deg, #fff5fa, #ffeaf3);
  border: 2px solid #ffddea;
}
.lw-play {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-height: 250px;
}
.lw-play-title {
  font-size: 15px;
  color: #6b4a5a;
  margin: 0;
}

/* 通用按钮 */
.lw-btn {
  border: none;
  border-radius: 999px;
  padding: 8px 22px;
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 111, 165, 0.35);
  transition: all 0.2s ease;
}
.lw-btn:hover:not(:disabled) { transform: translateY(-2px); }
.lw-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.lw-btn-ghost {
  background: #fff;
  border: 2px solid #ffd3e4;
  color: #b06a8a;
  box-shadow: none;
}
.lw-btn.picked {
  box-shadow: inset 0 0 0 3px #fff, 0 4px 12px rgba(255, 111, 165, 0.4);
}
.lw-btn em {
  font-style: normal;
  font-size: 11px;
  background: rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  padding: 1px 7px;
  margin-left: 4px;
}
.lw-num {
  width: 40px;
  padding: 8px 0;
  text-align: center;
}
.lw-actions {
  display: flex;
  gap: 10px;
}
.lw-msg {
  font-size: 13px;
  color: #b06a8a;
  min-height: 18px;
  margin: 0;
  text-align: center;
}
.lw-msg.ok {
  color: #ff5fa0;
  font-weight: 700;
}

/* ---------- ① 礼物盒 ---------- */
.lw-box-row {
  display: flex;
  gap: 18px;
  position: relative;
}
.lw-box {
  width: 86px;
  height: 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(150deg, #ff9ecb, #ff6fa5);
  border: 3px solid #fff;
  box-shadow: 0 6px 16px rgba(255, 111, 165, 0.4);
  cursor: pointer;
  font-size: 40px;
  transition: transform 0.15s ease;
  user-select: none;
}
.lw-box:hover { transform: scale(1.08); }
.lw-box.reveal {
  background: linear-gradient(150deg, #ffe9f3, #ffd3e6);
  border-color: #ffd3e4;
  box-shadow: 0 4px 12px rgba(255, 158, 198, 0.3);
}
.lw-move-move { transition: transform 0.35s ease; }
.lw-box-emoji { line-height: 1; }

/* ---------- ② 骰子 ---------- */
.lw-dice {
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 22px;
  background: #fff;
  border: 3px solid #ffd3e4;
  box-shadow: 0 8px 20px rgba(255, 158, 198, 0.35);
  font-size: 48px;
  color: #ff5fa0;
  transition: transform 0.1s ease;
}
.lw-dice.rolling {
  animation: lw-shake 0.12s infinite;
}
@keyframes lw-shake {
  0% { transform: rotate(-8deg) translateX(-2px); }
  50% { transform: rotate(8deg) translateX(2px); }
  100% { transform: rotate(-8deg) translateX(-2px); }
}
.lw-choice {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

/* ---------- ③ 硬币 ---------- */
.lw-coin {
  width: 96px;
  height: 96px;
  position: relative;
  transform-style: preserve-3d;
}
.lw-coin.flip {
  animation: lw-coinspin 0.9s ease-in-out;
}
@keyframes lw-coinspin {
  0% { transform: rotateY(0); }
  100% { transform: rotateY(1800deg); }
}
.lw-coin-face {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 42px;
  backface-visibility: hidden;
  border: 4px solid #ffd700;
  background: radial-gradient(circle at 35% 30%, #fff7d0, #ffe28a);
  box-shadow: 0 6px 16px rgba(255, 200, 60, 0.45);
}
.lw-coin-back {
  transform: rotateY(180deg);
  background: radial-gradient(circle at 35% 30%, #ffe3ee, #ffb3d4);
  border-color: #ff8fc7;
}

/* ---------- ④ 转盘 ---------- */
.lw-wheel-wrap {
  position: relative;
  width: 220px;
  height: 230px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.lw-pointer {
  font-size: 26px;
  color: #ff5fa0;
  line-height: 1;
  z-index: 3;
  text-shadow: 0 2px 4px rgba(255, 111, 165, 0.5);
}
.lw-wheel {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  position: relative;
  border: 6px solid #fff;
  box-shadow: 0 10px 24px rgba(255, 158, 198, 0.4);
  background: conic-gradient(
    #ff9ecb 0deg 45deg,
    #ffd98a 45deg 90deg,
    #c9a6ff 90deg 135deg,
    #8fe3c9 135deg 180deg,
    #7ec8ff 180deg 225deg,
    #ffab9e 225deg 270deg,
    #ffb3e6 270deg 315deg,
    #ffe6a8 315deg 360deg
  );
  transition: transform 2.2s cubic-bezier(0.15, 0.8, 0.15, 1);
}
.lw-sector {
  position: absolute;
  left: 50%;
  top: 50%;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}
.lw-tip {
  text-align: center;
  font-size: 12px;
  color: #c490a8;
  margin-top: 12px;
}
</style>
